import { useEffect } from "react";
import { useQueryClient } from "react-query";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { subscriptionInfoFromDetails } from './subscriptionInfo';
import { useGetUserSubscription, getUserSubscription } from "../queries/profile";
import { useEntitlementsRefresh } from "../utils/entitlementsRefresh";
import { useSelector, useDispatch } from 'react-redux';
import {
    setCompanyDetails,
    setLayerOptions,
    setSubscriptionInfo,
    setUserInfo
} from '../redux/Slices/Global/globalSlice';
import { Spinner } from "react-bootstrap";
import Layout from "../pages/Layout/Layout";
import NoPackageState from "../components/activation/NoPackageState";
import { derivePlanState, PLAN_RECHECK_EVENT } from "../utils/planState";
import OpeningDashboard, { peekOpeningFlag, consumeOpeningFlag } from "../components/auth/OpeningDashboard";

// Pages a company without an active package can still open: its own profile
// (change password, account) — everything else is replaced by NoPackageState.
const NO_PLAN_ALLOWED = ["/profile"];

const CompanyDetailsRouter = () => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { rerenderSubscription } = useSelector((state) => state.global);

    // Add enabled option and staleTime to optimize refetching
    const { data: subscriptionData, isLoading } = useGetUserSubscription({
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        refetchOnMount: true,
        refetchOnWindowFocus: false
    });

    // A package change reaches this session on focus / on a feature_disabled 402, no reload needed.
    useEntitlementsRefresh(getUserSubscription);

    useEffect(() => {
        if (!subscriptionData) return;

        const updateGlobalState = () => {
            // Update company and user details
            dispatch(setCompanyDetails(subscriptionData));
            dispatch(setUserInfo(subscriptionData?.user));

            // Process subscription status (see subscriptionInfo.js for the no-active-subscription rules).
            const subscriptionDetails = subscriptionInfoFromDetails(subscriptionData);
            dispatch(setSubscriptionInfo(subscriptionDetails));

            // Process layer options. Null-safe: a package whose `features` lacks
            // `multiple_layer` (e.g. a package edited without layers, or a tier
            // that omits it) must NOT crash the router — it wraps the entire app,
            // so an undefined .map() here white-screens every page for that user.
            const layerOptions = (subscriptionData?.package?.features?.multiple_layer || []).map(layer => ({
                name: layer.charAt(0).toUpperCase() + layer.slice(1).toLowerCase(),
                code: layer.toUpperCase()
            }));
            dispatch(setLayerOptions(layerOptions));
        };

        updateGlobalState();
    }, [subscriptionData]);

    // A protected request answered "subscription expired" (402) somewhere: the plan
    // changed under us (ended just now, or was deactivated). Re-read it; the state
    // below takes over. Axios stays silent about it (no toast).
    useEffect(() => {
        const recheck = () => queryClient.invalidateQueries('getUserSubscription');
        window.addEventListener(PLAN_RECHECK_EVENT, recheck);
        return () => window.removeEventListener(PLAN_RECHECK_EVENT, recheck);
    }, [queryClient]);

    // Handle rerender subscription
    useEffect(() => {
        if (rerenderSubscription) {
            queryClient.invalidateQueries('getUserSubscription');
        }
    }, [rerenderSubscription, queryClient]);

    // Right after a sign-in the wait IS the login's success state; once the data is in, the flag is spent.
    if (!isLoading) consumeOpeningFlag();
    if (isLoading && peekOpeningFlag()) return <OpeningDashboard />;

    if (isLoading) {
        return (
            <div className="flex w-[100vw] h-[100vh] items-center justify-center">
                <Spinner as="span" variant="primary" size='lg' animation="border" role="status" className='mx-3 my-5 ' aria-hidden="true" />
            </div>
        )
    }

    // No active package (never had one, or it ended): ONE state replaces the content
    // area of every page — sidebar navigation, deep links and refreshes alike — and
    // no page mounts, so no protected API call is made. Applies before the
    // restaurant redirect below (that one is for working accounts).
    if (derivePlanState(subscriptionData).status !== 'active') {
        const allowed = NO_PLAN_ALLOWED.some((p) => pathname === p || pathname.startsWith(p + "/"));
        if (!allowed) {
            return (
                <Layout padding={false}>
                    <NoPackageState details={subscriptionData} />
                </Layout>
            );
        }
        return <Outlet />;
    }

    // Restaurant (video-wall-only) accounts get a locked-down surface: the Video
    // Wall app, profile/settings, media upload, and device management are reachable.
    // Any other route (the old campaign dashboard, shows, content aggregator, apps
    // gallery, billing pages, or a hand-typed URL) redirects to the Video Wall
    // dashboard. Standard accounts are unaffected. Fail-open: unknown/missing
    // account_type → full app.
    const isRestaurant = subscriptionData?.company?.account_type === "restaurant";
    if (isRestaurant) {
        // Restaurant accounts also need to upload media (/media/*) and pair/manage
        // their devices (/devices/*, /screens/*) — same pages standard users use.
        const allowed = ["/apps/video-wall", "/profile", "/media", "/devices", "/screens", "/schedule", "/customers", "/marketing"];
        const ok = allowed.some((p) => pathname === p || pathname.startsWith(p + "/"));
        if (!ok) return <Navigate to="/apps/video-wall" replace />;
    }

    return <Outlet />;
};

export { CompanyDetailsRouter };
